import { ChangeDetectorRef, Component, OnChanges, OnInit, SimpleChanges, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../../components/dropdown/dropdown-item.component';
import { DropdownToggleDirective } from '../../../components/dropdown/dropdown-toggle.directive';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AccountGroup } from '../../../domain/account.domain';
import { AccountContextService } from '../services/account-context.service';
import { AccountGroupLabelComponent } from './account-group-label.component';

/**
 * Account Group Selector Component
 * Account group selector component consisting of a dropdown of account groups, together with localStorage component to
 * save selected account.
 *
 * Params:
 *
 * @param {Array} accountGroups List of account groups to show in the dropdown
 * @param {String} initAccountGroupId optional Account group id to select in dropdown.
 * @param {function} initAccountGroup optional Callback function is called when initial value for selected account group is set
 * @param {String} showPlaceholder optional Whether to show 'Please Select' placeholder
 * @param {number}
 *
 * @example
 * <td-wb-account-group-selector
 [accountGroups]="accountGroups"
 [(accountGroup)]="accountGroup"
 [initAccountGroupId]="initAccountGroupId"
 (initAccountGroupChange)="onInitAccountGroup($event)"
 [showPlaceholder]="true"
 ngDefaultControl>
 </td-wb-account-group-selector>
 */
@Component({
  selector: 'td-wb-account-group-selector',
  templateUrl: './account-group-selector.component.html',
  styleUrls: ['./account-group-selector.component.scss'],
  imports: [
    DropdownComponent,
    FormsModule,
    TranslatePipe,
    AccountGroupLabelComponent,
    DropdownItemComponent,
    DropdownToggleDirective
  ]
})
export class AccountGroupSelectorComponent implements OnInit, OnChanges {
  private readonly accountContextService = inject(AccountContextService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  static readonly DEFAULT_DROP_DOWN_MAX_SIZE: number = 10;

  /*
   * List of account groups to show in the dropdown
   */
  readonly accountGroups = input<Array<AccountGroup>>(undefined);
  /*
   * Maximum size of drop down options to show
   */
  readonly size = input<number>(AccountGroupSelectorComponent.DEFAULT_DROP_DOWN_MAX_SIZE);
  /*
   * Whether to show 'Please Select' placeholder
   */
  readonly showPlaceholder = input<boolean>(undefined);
  /*
   * Account group id to select in dropdown.
   */
  readonly initAccountGroupId = input<string>(undefined);

  readonly accountGroup = model<AccountGroup>(undefined);

  // initial account group change event
  readonly initAccountGroupChange = output<AccountGroup>();

  ngOnInit(): void {
    this.initSelectedAccountGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accountGroup !== undefined && changes.accountGroup.currentValue !== undefined) {
      this.storeAccountGroupId(changes.accountGroup.currentValue);
    }
  }

  storeAccountGroupId(accountGroup: AccountGroup): void {
    if (accountGroup !== undefined) {
      this.accountContextService.setSelectedAccountGroupId(accountGroup.groupId);
    }
  }

  onModelChange($event: AccountGroup): void {
    this.accountGroup.set($event);
  }

  private initSelectedAccountGroup(): void {
    this.accountContextService.resolveAccountGroup(this.accountGroups(), this.initAccountGroupId())
      .subscribe((accountGroup: AccountGroup): void => {
        Promise.resolve().then((): void => {
          this.accountGroup.set(accountGroup);
          this.storeAccountGroupId(accountGroup);
          if (this.initAccountGroupChange !== undefined) {
            this.initAccountGroupChange.emit(accountGroup);
          }
          this.changeDetectorRef.markForCheck();
        });
      });
  }
}
